import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Validate API base URL
if (!API_BASE_URL) {
  throw new Error('REACT_APP_API_URL environment variable is not configured');
}

// Validate URL format
function validateApiUrl(url: string): boolean {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.protocol === 'https:' && parsedUrl.hostname.includes('amazonaws.com');
  } catch {
    return false;
  }
}

if (!validateApiUrl(API_BASE_URL)) {
  throw new Error('Invalid API base URL format');
}

// Custom error for session expiry
export class SessionExpiredError extends Error {
  constructor(message: string = 'Your session has expired. Please log in again.') {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

// Custom error for permission denied (403 that's not session related)
export class PermissionDeniedError extends Error {
  constructor(message: string = 'You do not have permission to perform this action.') {
    super(message);
    this.name = 'PermissionDeniedError';
  }
}

// Event emitter for session expiry
type SessionExpiredCallback = () => void;
let sessionExpiredCallback: SessionExpiredCallback | null = null;

export function onSessionExpired(callback: SessionExpiredCallback) {
  sessionExpiredCallback = callback;
}

// Helper to get valid auth token with proper error handling
async function getAuthToken(useAccessToken: boolean = false): Promise<string> {
  try {
    const session = await fetchAuthSession();
    const token = useAccessToken 
      ? session.tokens?.accessToken?.toString()
      : session.tokens?.idToken?.toString();

    if (!token) {
      throw new SessionExpiredError();
    }

    return token;
  } catch (error) {
    if (error instanceof SessionExpiredError) {
      throw error;
    }
    // Any auth error means session is invalid
    console.error('Auth session error:', error);
    throw new SessionExpiredError();
  }
}

// Handle API response errors
async function handleApiError(response: Response, error?: Error): Promise<never> {
  // 401 is always session expired
  if (response.status === 401) {
    const sessionError = new SessionExpiredError();
    if (sessionExpiredCallback) {
      sessionExpiredCallback();
    }
    throw sessionError;
  }
  
  // 403 could be permission denied OR session expired (depending on the error message)
  if (response.status === 403) {
    try {
      const errorBody = await response.json();
      const errorMessage = errorBody.error || errorBody.message || '';
      
      // Check if it's an auth/session related 403
      const isSessionError = errorMessage.toLowerCase().includes('token') ||
                            errorMessage.toLowerCase().includes('session') ||
                            errorMessage.toLowerCase().includes('unauthorized') ||
                            errorMessage.toLowerCase().includes('access denied');
      
      if (isSessionError) {
        const sessionError = new SessionExpiredError();
        if (sessionExpiredCallback) {
          sessionExpiredCallback();
        }
        throw sessionError;
      }
      
      // It's a permission denied error - throw with the actual message
      throw new PermissionDeniedError(errorMessage || 'You do not have permission to perform this action.');
    } catch (parseError) {
      if (parseError instanceof SessionExpiredError || parseError instanceof PermissionDeniedError) {
        throw parseError;
      }
      // If we can't parse the response, treat as permission denied
      throw new PermissionDeniedError();
    }
  }
  
  // For other errors, try to get the error message from response
  try {
    const errorBody = await response.json();
    throw new Error(errorBody.error || errorBody.message || response.statusText);
  } catch (parseError) {
    if (parseError instanceof Error && parseError.message !== response.statusText) {
      throw parseError;
    }
    throw error || new Error('Request failed: ' + response.statusText);
  }
}

export async function apiCall(endpoint: string, options: RequestInit = {}) {
  try {
    const token = await getAuthToken(false); // Use ID token
    
    // Sanitize endpoint to prevent path traversal
    const sanitizedEndpoint = endpoint.replace(/\.\./g, '');

    const response = await fetch(API_BASE_URL + sanitizedEndpoint, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token,
        ...options.headers,
      },
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return await response.json();
  } catch (error) {
    if (error instanceof SessionExpiredError) {
      if (sessionExpiredCallback) {
        sessionExpiredCallback();
      }
      throw error;
    }
    if (error instanceof PermissionDeniedError) {
      // Don't trigger session expiry for permission errors
      throw error;
    }
    console.error('API call failed:', error);
    throw error;
  }
}

// MFA API call - uses Access Token instead of ID Token
// Cognito MFA APIs (associateSoftwareToken, verifySoftwareToken, etc.) require Access Token
export async function mfaApiCall(endpoint: string, options: RequestInit = {}) {
  try {
    const token = await getAuthToken(true); // Use Access token for MFA
    
    // Sanitize endpoint to prevent path traversal
    const sanitizedEndpoint = endpoint.replace(/\.\./g, '');

    const response = await fetch(API_BASE_URL + sanitizedEndpoint, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token,
        ...options.headers,
      },
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return await response.json();
  } catch (error) {
    if (error instanceof SessionExpiredError) {
      if (sessionExpiredCallback) {
        sessionExpiredCallback();
      }
      throw error;
    }
    if (error instanceof PermissionDeniedError) {
      throw error;
    }
    console.error('MFA API call failed:', error);
    throw error;
  }
}

// Public API call for auth endpoints that don't require authentication
export async function publicApiCall(endpoint: string, options: RequestInit = {}) {
  try {
    // Sanitize endpoint to prevent path traversal
    const sanitizedEndpoint = endpoint.replace(/\.\./g, '');

    const response = await fetch(API_BASE_URL + sanitizedEndpoint, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error('Request failed: ' + response.statusText);
    }

    return await response.json();
  } catch (error) {
    console.error('Public API call failed:', error);
    throw error;
  }
}
